// C:\Projects\texe-web\src\pages\TagFeed.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "../lib/api";
import PostCard from "../components/PostCard";
import type { PostItem } from "../components/PostCard";

type AnyObj = Record<string, any>;

// backend farklı alan adları dönerse tek yerden düzeltelim
function normalizePost(raw: AnyObj | null | undefined): PostItem | null {
  if (!raw || typeof raw !== "object") return null;

  const id = raw.id ?? raw._id ?? raw.postId;
  const text = raw.text ?? raw.content ?? "";
  const createdAt = raw.createdAt ?? raw.created_at ?? raw.time;

  if (!id || !text || !createdAt) return null;

  const aRaw = raw.author ?? raw.user ?? raw.owner ?? null;
  const author =
    aRaw && typeof aRaw === "object"
      ? {
          id: aRaw.id ?? aRaw._id,
          username: aRaw.username ?? aRaw.name,
          email: aRaw.email,
          avatarUrl: aRaw.avatarUrl ?? aRaw.avatar,
          avatarUrlSmall: aRaw.avatarUrlSmall ?? aRaw.avatarSmall ?? aRaw.avatarUrl,
        }
      : null;

  return {
    id: String(id),
    text: String(text),
    createdAt: String(createdAt),
    author,
  };
}

export default function TagFeed() {
  const { name } = useParams<{ name: string }>();
  const tag = useMemo(() => (name ?? "").trim().toLowerCase(), [name]);

  const [items, setItems] = useState<PostItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [eof, setEof] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadMore(initial = false) {
    if (loading || eof || !tag) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set("tag", tag);
      if (cursor) qs.set("cursor", cursor);

      const res: any = await apiGet(`/api/posts?${qs.toString()}`);

      // {items: []} | {data: []} | [] destekle
      const rawList: AnyObj[] = Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
        ? res
        : [];

      const normalized = rawList.map(normalizePost).filter((p): p is PostItem => !!p);

      const nextCursor: string | null =
        (typeof res?.nextCursor === "string" ? res.nextCursor : null) ??
        (typeof res?.cursor === "string" ? res.cursor : null) ??
        null;

      setItems((prev) => (initial ? normalized : [...prev, ...normalized]));
      setCursor(nextCursor);
      setEof(!nextCursor || normalized.length === 0);
    } catch (e: any) {
      setError(e?.message ?? "Bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setItems([]);
    setCursor(null);
    setEof(false);
    setError(null);
    if (tag) loadMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  if (!tag) {
    return (
      <div className="p-4">
        <p>Geçersiz etiket.</p>
        <Link to="/">Ana sayfaya dön</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-3 py-4">
      <header className="mb-3">
        <h1 className="text-xl font-semibold">#{tag}</h1>
        <p className="opacity-70 text-sm">Etikete göre akış</p>
      </header>

      {items.length === 0 && !loading && !error && (
        <div className="opacity-70 text-sm p-4">Bu etiketle henüz içerik yok.</div>
      )}
      {error && (
        <div className="text-red-400 text-sm p-3 border border-red-900/40 rounded">
          {error}{" "}
          <button className="underline" onClick={() => loadMore(items.length === 0)}>
            Tekrar dene
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {items.map((p) => (
          <PostCard key={p.id} item={p} />
        ))}
      </div>

      <div className="mt-4 flex justify-center">
        {!eof && (
          <button
            onClick={() => loadMore()}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-[var(--muted)] disabled:opacity-60"
          >
            {loading ? "Yükleniyor…" : "Daha fazla yükle"}
          </button>
        )}
        {eof && items.length > 0 && (
          <div className="opacity-60 text-sm py-2">Hepsi bu kadar 🎉</div>
        )}
      </div>
    </div>
  );
}
